// src/api/api.ts
import axios from 'axios';
import { getOrCreateDeviceId } from '../helpers/device';
import { setRemainingSeconds } from './auth/sessionTTL';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000/api',
});

// Domyślny Content-Type tylko dla JSON (uploady dostaną boundary od przeglądarki)
api.defaults.headers.post['Content-Type'] = 'application/json';

function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  const keys = ['token', 'access_token', 'authToken', 'jwt'];
  for (const k of keys) {
    const v = localStorage.getItem(k) || sessionStorage.getItem(k);
    if (v) return v;
  }
  return null;
}

api.interceptors.request.use((config) => {
  try {
    // przygotuj nagłówki w sposób zgodny z Axios v1
    let h: any = config.headers as any;
    if (!h || typeof h !== 'object') h = {};
    const setHdr = (name: string, value: string) => {
      if (h?.set && typeof h.set === 'function') h.set(name, value);
      else h[name] = value;
    };
    const delHdr = (name: string) => {
      if (h?.delete && typeof h.delete === 'function') h.delete(name);
      else delete h[name];
    };
    const hasHdr = (name: string) =>
      (h?.has && typeof h.has === 'function') ? h.has(name) : Object.prototype.hasOwnProperty.call(h, name);

    // Device headers
    const deviceId = getOrCreateDeviceId();
    const deviceName = typeof navigator !== 'undefined' ? navigator.userAgent || 'web' : 'web';
    setHdr('X-Device-Id', deviceId);
    setHdr('X-Device-Name', deviceName);

    // Auth header (tylko Authorization — bez X-Auth-Token, aby nie wywoływać CORS preflighta z nie-dozwolonym nagłówkiem)
    const token = getToken();
    if (token && !hasHdr('Authorization')) {
      setHdr('Authorization', `Bearer ${token}`);
    }

    // Content-Type: usuń przy FormData (upload), inaczej ustaw JSON gdy brak
    const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData;
    if (isFormData) {
      delHdr('Content-Type'); // przeglądarka doda boundary sama
    } else if (!hasHdr('Content-Type')) {
      setHdr('Content-Type', 'application/json');
    }

    config.headers = h;
  } catch {
    // ignore
  }
  return config;
});

export default api;

api.interceptors.response.use((resp) => {
  try {
    const h = resp.headers || {};
    const url = String(resp.config?.url || '');

    const respToken = (resp.data && (resp.data.token || resp.data.accessToken)) as string | undefined;
    if (respToken) {
      try {
        const base64 = respToken.split('.')[1];
        const json = atob(base64.replace(/-/g, '+').replace(/_/g, '/'));
        const payload = JSON.parse(json);
        if (payload?.exp) {
          const rem = Math.max(0, Math.floor(payload.exp - Date.now() / 1000));
          setRemainingSeconds(rem);
        }
      } catch { }
      return resp;
    }

    const left = h['x-token-remaining-seconds'];
    if (left != null) {
      const sec = Number(left);
      if (!Number.isNaN(sec)) setRemainingSeconds(sec);
    } else if (!/\/auth\/refresh(?:\?|$)/.test(url)) {
      const auth = (resp.config?.headers as any)?.Authorization || (resp.config?.headers as any)?.authorization;
      const token = typeof auth === 'string' && auth.startsWith('Bearer ') ? auth.slice(7) : null;
      if (token) {
        const base64 = token.split('.')[1];
        const json = atob(base64.replace(/-/g, '+').replace(/_/g, '/'));
        const payload = JSON.parse(json);
        if (payload?.exp) {
          const rem = Math.max(0, Math.floor(payload.exp - Date.now() / 1000));
          setRemainingSeconds(rem);
        }
      }
    }
  } catch { }
  return resp;
}, (err) => Promise.reject(err));
